import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import Step from '@perpay-web/components/base/Step/Step';
import WarningLoaderCircle from '@perpay-web/components/base/WarningLoaderCircle/WarningLoaderCircle';
import PaymentSetupCallContainer from '@perpay-web/fintech/components/containers/PaymentSetupCallContainer';
import PayrollInstructionsContainer from '@perpay-web/fintech/components/containers/PayrollInstructionsContainer';
import { PAYMENT_SETUP_PHONE_NUMBERS } from '@perpay-web/fintech/constants/paymentSetupPhoneNumbers';
import { useHeaderRightNav } from '@perpay-web/fintech/hooks/useHeaderRightNavContext';
import { useConstCallback } from '@perpay-web/hooks/useConstCallback';
import { useSteps } from '@perpay-web/hooks/useSteps';
import { randomElementOf } from '@perpay-web/utils/arrayUtils';
import { noop } from '@perpay-web/utils/noop';

import styles from './PayrollInstructionsFlow.scss';

const INSTRUCTIONS_STEP = 'instructions';
const SETUP_CALL_STEP = 'setupCall';

const steps = [INSTRUCTIONS_STEP, SETUP_CALL_STEP];

const PayrollInstructionsFlow = ({
    resetPayrollInstructions,
    inModal = false,
    onClickBackFromModal = noop,
    onClickNeedHelpFromModal = noop,
    onClickSetUpPaymentsLater,
    onClickInstructionsBack = noop,
    instructionsType,
    onClickFinished,
    closeWhenSupportToolLaunched = false,
    loading,
    onMount,
    analyticsPayrollInstructionsViewed,
    flowType = null,
}) => {
    const navigate = useNavigate();
    const [experimentPhoneNumber] = useState(
        randomElementOf(PAYMENT_SETUP_PHONE_NUMBERS),
    );
    const initialStep = INSTRUCTIONS_STEP;
    const { step, setStep, goBack } = useSteps(steps, initialStep);

    useEffect(() => {
        onMount();
        analyticsPayrollInstructionsViewed();

        return () => {
            resetPayrollInstructions();
        };
    }, [analyticsPayrollInstructionsViewed, onMount, resetPayrollInstructions]);

    const onClickSetUpPaymentsLaterCb = useConstCallback(
        onClickSetUpPaymentsLater,
    );
    const renderHeaderCb = useCallback(
        () => (
            <>
                <Step
                    name={INSTRUCTIONS_STEP}
                    current={step}
                    render={() => (
                        <CustomButton
                            action='link'
                            className={styles.back}
                            onClick={() => {
                                if (onClickInstructionsBack) {
                                    onClickInstructionsBack();
                                } else {
                                    navigate(-1);
                                }
                                onClickSetUpPaymentsLaterCb();
                            }}
                            size='small'
                        >
                            <span className={styles.name__block}>Back</span>
                        </CustomButton>
                    )}
                />
                <Step
                    name={SETUP_CALL_STEP}
                    current={step}
                    render={() => (
                        <CustomButton
                            action='link'
                            className={styles.back}
                            onClick={() => {
                                navigate(-1);
                                onClickSetUpPaymentsLaterCb();
                            }}
                            size='small'
                        >
                            <span className={styles.name__block}>Back</span>
                        </CustomButton>
                    )}
                />
            </>
        ),
        [step, onClickInstructionsBack, onClickSetUpPaymentsLaterCb, navigate],
    );
    useHeaderRightNav(renderHeaderCb);

    const onClickNeedHelp = useCallback(() => {
        onClickNeedHelpFromModal();
        setStep(SETUP_CALL_STEP);
    }, [onClickNeedHelpFromModal, setStep]);

    const onClickBack = useCallback(() => {
        onClickBackFromModal();
        goBack();
    }, [goBack, onClickBackFromModal]);

    if (loading) {
        return <WarningLoaderCircle />;
    }

    return (
        <>
            <Step
                name={INSTRUCTIONS_STEP}
                current={step}
                render={() => (
                    <PayrollInstructionsContainer
                        onClickFinished={onClickFinished}
                        closeWhenSupportToolLaunched={
                            closeWhenSupportToolLaunched
                        }
                        instructionsType={instructionsType}
                        onClickNeedHelp={onClickNeedHelp}
                        flowType={flowType}
                    />
                )}
            />
            <Step
                name={SETUP_CALL_STEP}
                current={step}
                render={() => (
                    <PaymentSetupCallContainer
                        phoneNumber={experimentPhoneNumber}
                        onClickBack={onClickBack}
                        inModal={inModal}
                    />
                )}
            />
        </>
    );
};

export default PayrollInstructionsFlow;
